import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { currentLocale } from "../locales";
import { ProcessDefinitionProps, ProcessModePolicy } from "../types/processDefinition";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The single unit every stored size number (item sizes, material sizes,
 * pipe cutting/leftover figures) is understood to be in. Chosen once when
 * the project is set up (REACT_APP_SIZE_UNIT) — every size in the DB is a
 * plain decimal, this only controls what label is shown next to it.
 * Changing it later relabels existing numbers, it never recalculates them.
 */
export const SIZE_UNIT = process.env.REACT_APP_SIZE_UNIT || "mm";

/**
 * Renders a stored size number with the configured unit label
 * (e.g. 10.5 -> "10.5 inch"), trimming trailing zeros. Used everywhere a
 * size (item size or material size — same shape) is displayed.
 */
export function formatSize(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "-";
  return `${num} ${SIZE_UNIT}`;
}

export function localizedProcessName(definition: ProcessDefinitionProps): string {
  if (currentLocale === "gu" && definition.name_gu) return definition.name_gu;
  return definition.name;
}

type OrderMode = "in_house" | "outsource";

/**
 * Mirrors ProcessDefinition::allowedModes() on the backend. A sub-process
 * policy (when set) narrows the process definition's — an empty result
 * means the two disagree and the caller must treat that as a conflict, not
 * silently pick a side.
 */
export function allowedModes(
  definitionPolicy: ProcessModePolicy | null | undefined,
  subProcessPolicy?: ProcessModePolicy | null
): OrderMode[] {
  const modesFor = (policy: ProcessModePolicy | null | undefined): OrderMode[] => {
    if (policy === "in_house") return ["in_house"];
    if (policy === "outsource") return ["outsource"];
    return ["in_house", "outsource"];
  };
  const definitionModes = modesFor(definitionPolicy);
  if (!subProcessPolicy) return definitionModes;
  const subProcessModes = modesFor(subProcessPolicy);
  return definitionModes.filter((m) => subProcessModes.includes(m));
}

export function defaultMode(allowed: OrderMode[]): OrderMode {
  return allowed.length === 1 ? allowed[0] : "in_house";
}

export function formatSizeLabel(
  size?:
    | {
        name?: string | null;
        size_name?: string | null;
        // material_sizes.size is a real number (e.g. from a flattened stock
        // report row) — accepted alongside the free-text name/size_name keys
        // so every call site can pass its row through as-is.
        size?: number | null;
        material_name?: string | null;
      }
    | null
): string {
  if (!size) return "-";
  const dimension =
    size.name ?? size.size_name ?? (size.size != null ? formatSize(size.size) : null);
  if (size.material_name && dimension) return `${size.material_name} (${dimension})`;
  if (size.material_name) return size.material_name;
  return dimension || "-";
}
